<script>
/**
 * EngajamentoUsuariosModal — detalhamento do engajamento por usuário.
 *
 * Desktop: modal centralizado com tabela (Teleport + tokens de tema).
 * Mobile: abre em TELA CHEIA (desliza de baixo), com botão de voltar e
 * fechamento pelo back nativo (history.pushState, igual grupos/conversas).
 * A lista vira cards por usuário (mais legível que a tabela num tela estreita).
 */
const COLUNAS = [
  { key: 'mensagensConversa', label: 'Conversa', icon: 'ri-chat-3-line' },
  { key: 'mensagensGrupo', label: 'Grupos', icon: 'ri-group-line' },
  { key: 'mensagensCanal', label: 'Canais', icon: 'ri-broadcast-line' },
  { key: 'campanhas', label: 'Campanhas', icon: 'ri-megaphone-line' },
  { key: 'contatosCriados', label: 'Contatos', icon: 'ri-user-add-line' },
  { key: 'notas', label: 'Notas', icon: 'ri-sticky-note-line' },
  { key: 'logins', label: 'Acessos', icon: 'ri-login-circle-line' },
];

export default {
  name: 'EngajamentoUsuariosModal',
  props: {
    visivel: { type: Boolean, default: false },
    dados: { type: Object, default: () => ({}) },
  },
  emits: ['fechar'],
  data() {
    return { colunas: COLUNAS, sortKey: 'total', sortDir: 'desc' };
  },
  computed: {
    periodo() { return this.dados && this.dados.periodo; },
    resumo() { return (this.dados && this.dados.resumo) || {}; },
    usuarios() { return (this.dados && this.dados.porUsuario) || []; },
    maxTotal() { return this.usuarios.reduce((m, u) => Math.max(m, u.total || 0), 0) || 1; },
    sortChips() { return [{ key: 'total', label: 'Total' }, ...this.colunas.map((c) => ({ key: c.key, label: c.label }))]; },
    linhas() {
      const k = this.sortKey; const dir = this.sortDir === 'asc' ? 1 : -1;
      return [...this.usuarios].sort((a, b) => {
        if (k === 'nome') return String(a.nome || '').localeCompare(String(b.nome || '')) * dir;
        if (k === 'ultimoAcesso') return ((new Date(a.ultimoAcesso || 0)) - (new Date(b.ultimoAcesso || 0))) * dir;
        return ((a[k] || 0) - (b[k] || 0)) * dir;
      });
    },
  },
  watch: {
    visivel(v) { if (v) this._onOpen(); else this._onClose(); },
  },
  mounted() {
    this._pushed = false;
    this._onEsc = (e) => { if (e.key === 'Escape') this.fechar(); };
    this._onPop = () => { this._pushed = false; this.$emit('fechar'); };
    if (this.visivel) this._onOpen();
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this._onEsc);
    window.removeEventListener('popstate', this._onPop);
    document.body.classList.remove('eng-modal-open');
  },
  methods: {
    _ehMobile() { return window.matchMedia('(max-width: 767.98px)').matches; },
    _onOpen() {
      document.addEventListener('keydown', this._onEsc);
      document.body.classList.add('eng-modal-open');
      // Mobile: empurra um estado no histórico pra o "voltar" nativo fechar a
      // tela em vez de trocar de rota (mesmo padrão de grupos/conversas).
      if (this._ehMobile()) {
        window.history.pushState({ engModal: true }, '');
        this._pushed = true;
        window.addEventListener('popstate', this._onPop);
      }
    },
    _onClose() {
      document.removeEventListener('keydown', this._onEsc);
      window.removeEventListener('popstate', this._onPop);
      document.body.classList.remove('eng-modal-open');
      // Se fechou sem passar pelo back (ex.: desktop/Esc) e havíamos empurrado
      // um estado, consome-o pra não sujar o histórico.
      if (this._pushed) {
        this._pushed = false;
        if (window.history.state && window.history.state.engModal) window.history.back();
      }
    },
    fechar() {
      // Mobile com estado empurrado: volta pelo histórico (popstate → fecha).
      if (this._pushed) window.history.back();
      else this.$emit('fechar');
    },
    fmt(n) { return Number(n || 0).toLocaleString('pt-BR'); },
    ordenar(key) {
      if (this.sortKey === key) this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      else { this.sortKey = key; this.sortDir = key === 'nome' ? 'asc' : 'desc'; }
    },
    iniciais(nome) {
      const p = String(nome || '?').trim().split(/\s+/);
      return ((p[0] || '')[0] || '?').concat((p[1] || '')[0] || '').toUpperCase();
    },
    acesso(d) {
      if (!d) return 'nunca';
      try {
        const data = new Date(d); const hoje = new Date();
        if (data.toDateString() === hoje.toDateString()) return `hoje ${data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
        return data.toLocaleDateString('pt-BR');
      } catch (_e) { return '—'; }
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <transition name="eng-t">
      <div v-if="visivel" class="eng-overlay" @click.self="fechar">
        <div class="eng-panel">
          <header class="eng-head">
            <button type="button" class="eng-back" title="Voltar" @click="fechar">
              <i class="ri-arrow-left-line"></i>
            </button>
            <div class="eng-head-txt">
              <h6 class="m-0"><i class="ri-team-line me-1 d-none d-md-inline"></i>Engajamento por usuário</h6>
              <small class="text-muted">
                {{ fmt(resumo.usuariosAtivos) }} de {{ fmt(resumo.totalUsuarios) }} ativos
                <span v-if="periodo"> · últimos {{ periodo }} dias</span>
              </small>
            </div>
            <button type="button" class="eng-close" title="Fechar" @click="fechar">
              <i class="ri-close-line"></i>
            </button>
          </header>

          <!-- Barra de ordenação (mobile) -->
          <div class="eng-sortbar">
            <span class="eng-sort-lbl">Ordenar:</span>
            <button
              v-for="s in sortChips"
              :key="s.key"
              type="button"
              class="eng-sortchip"
              :class="{ active: sortKey === s.key }"
              @click="ordenar(s.key)"
            >
              {{ s.label }}
              <i v-if="sortKey === s.key" :class="sortDir === 'asc' ? 'ri-arrow-up-s-fill' : 'ri-arrow-down-s-fill'"></i>
            </button>
          </div>

          <div class="eng-body">
            <div v-if="!usuarios.length" class="eng-empty">
              <i class="ri-user-line"></i>
              <div>Nenhum usuário para exibir.</div>
            </div>

            <!-- ===== Desktop: tabela ===== -->
            <div v-else class="eng-tscroll">
              <table class="table table-hover table-sm align-middle eng-table m-0">
                <thead>
                  <tr>
                    <th class="eng-sticky" role="button" @click="ordenar('nome')">
                      Usuário <i v-if="sortKey === 'nome'" :class="sortDir === 'asc' ? 'ri-arrow-up-s-fill' : 'ri-arrow-down-s-fill'"></i>
                    </th>
                    <th v-for="c in colunas" :key="c.key" class="text-center" role="button" @click="ordenar(c.key)" :title="c.label">
                      <i :class="c.icon"></i>
                      <span class="d-none d-xl-inline ms-1">{{ c.label }}</span>
                      <i v-if="sortKey === c.key" :class="sortDir === 'asc' ? 'ri-arrow-up-s-fill' : 'ri-arrow-down-s-fill'"></i>
                    </th>
                    <th class="text-end" role="button" @click="ordenar('ultimoAcesso')">Acesso</th>
                    <th class="eng-total-col" role="button" @click="ordenar('total')">
                      Total <i v-if="sortKey === 'total'" :class="sortDir === 'asc' ? 'ri-arrow-up-s-fill' : 'ri-arrow-down-s-fill'"></i>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="u in linhas" :key="u.id" :class="{ 'eng-inativo': !u.total }">
                    <td class="eng-sticky">
                      <div class="d-flex align-items-center gap-2">
                        <span class="eng-av">{{ iniciais(u.nome) }}</span>
                        <span class="text-truncate eng-nome">{{ u.nome }}</span>
                        <span v-if="u.ativo === false" class="eng-badge-off">off</span>
                      </div>
                    </td>
                    <td v-for="c in colunas" :key="c.key" class="text-center">
                      <span :class="u[c.key] ? '' : 'text-muted opacity-50'">{{ fmt(u[c.key]) }}</span>
                    </td>
                    <td class="text-end"><small class="text-muted">{{ acesso(u.ultimoAcesso) }}</small></td>
                    <td class="eng-total-col">
                      <div class="eng-bar-wrap">
                        <div class="eng-bar" :style="{ width: Math.round((u.total / maxTotal) * 100) + '%' }"></div>
                        <span class="eng-bar-val">{{ fmt(u.total) }}</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- ===== Mobile: cards ===== -->
            <div v-if="usuarios.length" class="eng-cards">
              <div v-for="u in linhas" :key="u.id" class="eng-ucard" :class="{ 'eng-inativo': !u.total }">
                <div class="eng-uc-head">
                  <span class="eng-av eng-av-lg">{{ iniciais(u.nome) }}</span>
                  <div class="eng-uc-id">
                    <div class="eng-uc-name">
                      {{ u.nome }}
                      <span v-if="u.ativo === false" class="eng-badge-off">off</span>
                    </div>
                    <small class="text-muted">Último acesso: {{ acesso(u.ultimoAcesso) }}</small>
                  </div>
                  <div class="eng-uc-total">
                    <b>{{ fmt(u.total) }}</b>
                    <small>ações</small>
                  </div>
                </div>
                <div class="eng-uc-bar"><div class="eng-uc-fill" :style="{ width: Math.round((u.total / maxTotal) * 100) + '%' }"></div></div>
                <div class="eng-uc-grid">
                  <div v-for="c in colunas" :key="c.key" class="eng-uc-stat">
                    <span class="eng-uc-v" :class="{ 'text-muted opacity-50': !u[c.key] }">{{ fmt(u[c.key]) }}</span>
                    <span class="eng-uc-l"><i :class="c.icon"></i>{{ c.label }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <footer class="eng-foot">
            <small class="text-muted">Conversa/notas vêm do histórico; grupos/canais/campanhas/cadastros do log de atividade.</small>
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="fechar">Fechar</button>
          </footer>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
/* ===================== Overlay / painel ===================== */
.eng-overlay {
  position: fixed; inset: 0; background: rgba(0, 0, 0, 0.45); z-index: 1055;
  display: flex; align-items: center; justify-content: center; padding: 1rem;
}
.eng-panel {
  background: var(--vz-body-bg); color: var(--vz-body-color); border-radius: 0.75rem;
  width: 100%; max-width: 90vw; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
}
.eng-head {
  display: flex; align-items: center; gap: 0.6rem; padding: 0.7rem 1rem; flex-shrink: 0;
  border-bottom: 1px solid var(--vz-border-color);
}
.eng-head-txt { flex: 1; min-width: 0; }
.eng-head-txt h6 { font-weight: 600; }
.eng-back, .eng-close {
  border: 0; background: transparent; color: var(--vz-body-color); cursor: pointer;
  width: 36px; height: 36px; border-radius: 9px; display: inline-flex; align-items: center; justify-content: center; font-size: 1.35rem; flex-shrink: 0;
}
.eng-back:hover, .eng-close:hover { background: var(--vz-secondary-bg); }
.eng-back { display: none; } /* só no mobile */
.eng-body { overflow: auto; flex: 1; }
.eng-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 0.75rem;
  padding: 0.6rem 1rem; flex-shrink: 0; border-top: 1px solid var(--vz-border-color);
}
.eng-foot small { line-height: 1.25; }
.eng-empty { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; color: var(--vz-secondary-color); text-align: center; padding: 3rem 1rem; }
.eng-empty i { font-size: 2rem; opacity: 0.5; }

/* ===================== Barra de ordenação ===================== */
.eng-sortbar { display: none; align-items: center; gap: 0.4rem; padding: 0.5rem 0.75rem; overflow-x: auto; border-bottom: 1px solid var(--vz-border-color); flex-shrink: 0; -webkit-overflow-scrolling: touch; }
.eng-sort-lbl { font-size: 0.72rem; color: var(--vz-secondary-color); font-weight: 600; flex-shrink: 0; }
.eng-sortchip {
  flex-shrink: 0; border: 1px solid var(--vz-border-color); background: transparent; color: var(--vz-body-color);
  font-size: 0.75rem; font-weight: 600; padding: 5px 11px; border-radius: 999px; cursor: pointer; white-space: nowrap;
}
.eng-sortchip.active { background: #405189; color: #fff; border-color: #405189; }

/* ===================== Avatar / badges (comum) ===================== */
.eng-av {
  width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 0.7rem; font-weight: 700; color: #fff; background: #405189;
}
.eng-av-lg { width: 40px; height: 40px; font-size: 0.85rem; }
.eng-badge-off {
  flex-shrink: 0; font-size: 0.6rem; font-weight: 700; text-transform: uppercase;
  padding: 1px 5px; border-radius: 5px; background: rgba(240, 101, 72, 0.15); color: #f06548;
}
.eng-inativo { opacity: 0.7; }

/* ===================== Tabela (desktop) ===================== */
.eng-tscroll { overflow-x: auto; }
.eng-table { font-size: 0.82rem; }
.eng-table thead th {
  position: sticky; top: 0; z-index: 2; white-space: nowrap; user-select: none;
  background: var(--vz-secondary-bg); color: var(--vz-secondary-color);
  font-weight: 600; font-size: 0.72rem; text-transform: uppercase; letter-spacing: .02em;
}
.eng-table thead th:hover { color: var(--vz-body-color); }
.eng-sticky { position: sticky; left: 0; z-index: 1; background: var(--vz-body-bg); min-width: 200px; }
.eng-table thead th.eng-sticky { z-index: 3; background: var(--vz-secondary-bg); }
.eng-inativo td { opacity: 0.65; }
.eng-nome { max-width: 150px; }
.eng-total-col { min-width: 130px; }
.eng-bar-wrap { position: relative; height: 20px; display: flex; align-items: center; }
.eng-bar { height: 20px; border-radius: 5px; min-width: 2px; background: linear-gradient(90deg, #405189, #6a7cc4); }
.eng-bar-val { position: absolute; right: 6px; font-weight: 700; font-size: 0.78rem; color: var(--vz-body-color); }

/* ===================== Cards (mobile) ===================== */
.eng-cards { display: none; flex-direction: column; gap: 0.6rem; padding: 0.75rem; }
.eng-ucard { border: 1px solid var(--vz-border-color); border-radius: 12px; padding: 0.75rem; background: var(--vz-secondary-bg); }
.eng-uc-head { display: flex; align-items: center; gap: 0.6rem; }
.eng-uc-id { flex: 1; min-width: 0; }
.eng-uc-name { font-weight: 600; font-size: 0.92rem; display: flex; align-items: center; gap: 0.35rem; }
.eng-uc-total { text-align: right; flex-shrink: 0; line-height: 1; }
.eng-uc-total b { font-size: 1.3rem; color: var(--vz-body-color); }
.eng-uc-total small { display: block; font-size: 0.62rem; color: var(--vz-secondary-color); }
.eng-uc-bar { height: 6px; border-radius: 4px; background: var(--vz-light); overflow: hidden; margin: 0.6rem 0; }
.eng-uc-fill { height: 100%; border-radius: 4px; background: linear-gradient(90deg, #405189, #6a7cc4); min-width: 2px; }
.eng-uc-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.5rem 0.4rem; }
.eng-uc-stat { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 1px; }
.eng-uc-v { font-size: 0.95rem; font-weight: 700; line-height: 1; color: var(--vz-body-color); }
.eng-uc-l { font-size: 0.6rem; color: var(--vz-secondary-color); display: flex; align-items: center; gap: 2px; }
.eng-uc-l i { font-size: 0.7rem; }

/* ===================== Transição ===================== */
.eng-t-enter-active, .eng-t-leave-active { transition: opacity 0.2s ease; }
.eng-t-enter-from, .eng-t-leave-to { opacity: 0; }

/* ===================== Mobile: tela cheia ===================== */
@media (max-width: 767.98px) {
  .eng-overlay { padding: 0; background: var(--vz-body-bg); align-items: stretch; }
  .eng-panel { max-width: 100%; height: 100dvh; max-height: 100dvh; border-radius: 0; box-shadow: none; }
  .eng-back { display: inline-flex; }
  .eng-close { display: none; }
  .eng-sortbar { display: flex; }
  .eng-foot { display: none; }
  .eng-tscroll { display: none; }   /* esconde a tabela no mobile */
  .eng-cards { display: flex; }      /* mostra os cards */
  /* Painel desliza de baixo. */
  .eng-t-enter-active .eng-panel, .eng-t-leave-active .eng-panel { transition: transform 0.25s ease; }
  .eng-t-enter-from .eng-panel, .eng-t-leave-to .eng-panel { transform: translateY(100%); }
}
</style>
